import { useQuery } from '@apollo/client';
import { Box, DialogActions, DialogContent } from '@mui/material';
import { styled } from '@mui/material/styles';
import { IMAGE_FILE_TYPES } from 'Components/FileUpload/Dropzone';
import Filter from 'Components/Filter/Filter';
import {
  EFilterType,
  TDocumentCategoryFilterValue,
  TFilter,
  TFilterEvent,
} from 'Components/Filter/filter.types';
import Loader from 'Components/Forms/Loader';
import SearchToolbar from 'Components/Forms/SearchToolbar';
import { useLogEvent } from 'hooks/useLogEvent';
import { DOCUMENTS_PER_PAGE } from 'utils/pagination.utils';
import { useCompanyPlanContext } from 'FeatureModules/Auth/Contexts/CompanyPlanContext';
import ThemeButton from 'DesignSystem/Buttons/ThemeButton/ThemeButton';
import DialogDefault from 'DesignSystem/Overlays/Dialog/DialogDefault/DialogDefault';
import Tabs, { ITab } from 'DesignSystem/Primitives/Tabs/Tabs';
import { GET_DOCUMENTS, GET_SHARED_DOCUMENTS } from 'graphql/secure/queries';
import moment from 'moment';
import React, { FC, useMemo, useState } from 'react';
import { ECustomerPlan } from 'FeatureModules/Auth/types/company.types';
import { IDefaultDialogProps } from 'types/dialog.types';
import { IBaseDocument } from 'FeatureModules/Document/types/document.types';
import { EDocumentCollaborationType } from 'FeatureModules/Document/types/document.types';
import { IGraphQlConnection } from 'types/core.types';
import { removeGraphConnections } from 'utils/graphql.utils';
import DocumentTable from 'FeatureModules/Document/Components/Tables/DocumentTable';
import { EDocumentsTableColumnKeys } from '../../utils/documentHeaderColumns';
import ActionContainer from 'Components/ActionContainer';
import EmptyState from 'Components/EmptyState';
import ErrorState from 'Components/ErrorState';
import InfiniteScrollWrapper from 'Components/InfiniteScrollWrapper';

export interface IDocumentSelectorProps extends IDefaultDialogProps {
  selectedDocumentIds?: string[];
  multiselect?: boolean;
  onSelect: (selectedIds: string[]) => void;
}

const DATE_FORMAT = 'YYYY-MM-DD';

const SearchbarContainer = styled(ActionContainer)(() => ({
  backgroundColor: 'transparent',
}));

const DOCUMENT_TABS = [
  {
    key: EDocumentCollaborationType.INTERNAL,
    label: 'Internal documents',
  },
  {
    key: EDocumentCollaborationType.SHARED,
    label: 'Shared with me',
    onlyForBuyer: true,
  },
];

interface IQueryState {
  searchTerm?: string;
  creationDateRange?: { startDate?: string; endDate?: string };
  categories?: TDocumentCategoryFilterValue;
  siteIds?: string[];
}

const SelectDocumentsDialog: FC<IDocumentSelectorProps> = ({
  open,
  onSelect,
  onClose,
  selectedDocumentIds = [],
  multiselect = true,
}) => {
  const { plan } = useCompanyPlanContext();
  const isPartner = plan === ECustomerPlan.FREEMIUM;
  const { logEvent } = useLogEvent();

  const [selectedIds, setSelectedIds] = useState<string[]>(selectedDocumentIds);
  const [internalQueryState, setInternalQueryState] = useState<IQueryState>({});
  const [documentCollaborationType, setDocumentCollaborationType] = useState(
    EDocumentCollaborationType.INTERNAL
  );

  const tabs: ITab<EDocumentCollaborationType>[] = useMemo(
    () => DOCUMENT_TABS.filter(({ onlyForBuyer }) => !onlyForBuyer || (onlyForBuyer && !isPartner)),
    [isPartner]
  );

  const { searchTerm, creationDateRange, categories, siteIds } = internalQueryState;

  const documentQuery =
    documentCollaborationType === EDocumentCollaborationType.SHARED
      ? GET_SHARED_DOCUMENTS
      : GET_DOCUMENTS;

  const { data, loading, error, fetchMore } = useQuery<
    { documents: IGraphQlConnection<IBaseDocument> } & {
      sharedDocuments: IGraphQlConnection<IBaseDocument>;
    }
  >(documentQuery, {
    variables: {
      filters: {
        searchTerm,
        creationDateRange,
        categories,
        siteIds,
        fileExtensions: ['pdf', 'csv', 'xls', 'xlsx', ...IMAGE_FILE_TYPES],
      },
      first: DOCUMENTS_PER_PAGE,
    },
  });

  const currentData: IGraphQlConnection<IBaseDocument> | undefined =
    documentCollaborationType === EDocumentCollaborationType.SHARED
      ? data?.sharedDocuments
      : data?.documents;

  const hasNextPage = !!currentData?.pageInfo?.hasNextPage;
  const endCursor = currentData?.pageInfo?.endCursor;

  const handleChangeTab = (documentCollaborationType: EDocumentCollaborationType) => {
    setDocumentCollaborationType(documentCollaborationType);
  };

  const filters: TFilter[] = useMemo(
    () => [
      {
        type: EFilterType.DOCUMENT_CATEGORY,
        value: categories,
      },
      {
        type: EFilterType.SITES,
        value: (siteIds?.filter(Boolean) || undefined) as string[] | undefined,
      },
      {
        type: EFilterType.TIME_FRAME,
        label: 'Upload date',
        value:
          creationDateRange?.startDate || creationDateRange?.endDate
            ? {
                startDate: creationDateRange?.startDate
                  ? new Date(creationDateRange.startDate)
                  : undefined,
                endDate: creationDateRange?.endDate
                  ? new Date(creationDateRange.endDate)
                  : undefined,
              }
            : null,
      },
    ],
    [categories, creationDateRange, siteIds]
  );

  // TODO: Move filtering to backend
  const documents = useMemo(() => {
    if (!currentData?.edges) {
      return [];
    }
    const allDocuments = removeGraphConnections(currentData);
    return allDocuments.filter(
      ({ expiryDate }) => !moment(expiryDate).isBefore(moment(new Date()))
    );
  }, [currentData]);

  const columns = useMemo(() => {
    const baseColumns = [
      EDocumentsTableColumnKeys.TITLE,
      EDocumentsTableColumnKeys.USAGE,
      EDocumentsTableColumnKeys.ISSUE_DATE,
      EDocumentsTableColumnKeys.EXPIRY_DATE,
    ];

    if (documentCollaborationType === EDocumentCollaborationType.SHARED) {
      return [...baseColumns, EDocumentsTableColumnKeys.OWNER];
    }

    return baseColumns;
  }, [documentCollaborationType]);

  const onSubmit = () => {
    onSelect(selectedIds);
    onClose?.();
  };

  const handlePageEndReached = () => {
    if (hasNextPage) {
      fetchMore({
        variables: {
          after: endCursor,
        },
      });
    }
  };

  const handleFilterChange = (event: TFilterEvent) => {
    switch (event.type) {
      case EFilterType.TIME_FRAME: {
        const { startDate, endDate } = event.value || {};

        setInternalQueryState(prev => ({
          ...prev,
          creationDateRange: {
            startDate: startDate ? moment(startDate).format(DATE_FORMAT) : undefined,
            endDate: endDate ? moment(endDate).format(DATE_FORMAT) : undefined,
          },
        }));
        break;
      }
      case EFilterType.DOCUMENT_CATEGORY: {
        setInternalQueryState(prev => ({
          ...event,
          categories: event.value,
        }));
        break;
      }
      case EFilterType.SITES: {
        setInternalQueryState(prev => ({
          ...event,
          siteIds: event.value || undefined,
        }));
        break;
      }
      default:
        break;
    }
    logEvent(`${documentCollaborationType}_DOCUMENTS_FILTERED`, { type: event.type });
  };

  const handleFilterReset = () => {
    setInternalQueryState({
      searchTerm: '',
      creationDateRange: undefined,
      categories: undefined,
      siteIds: undefined,
    });
  };

  const handleSearchTermChange = (searchTerm: string) => {
    setInternalQueryState({ searchTerm });
    logEvent(`${documentCollaborationType}_DOCUMENTS_SEARCHED`, { searchTerm });
  };

  let dialogContent;
  if (error) dialogContent = <ErrorState />;
  else if (!documents.length && loading) dialogContent = <Loader />;
  else {
    dialogContent = (
      <>
        <Box mb={1}>
          <Tabs tabs={tabs} selectedTab={documentCollaborationType} onChange={handleChangeTab} />
        </Box>

        <Box position="relative">
          <Filter
            filters={filters}
            onFilterChange={handleFilterChange}
            onFilterReset={handleFilterReset}
          />

          <SearchbarContainer>
            <SearchToolbar
              setSearchTerm={handleSearchTermChange}
              searchTerm={searchTerm || ''}
              data-testid="document-search-input"
              placeholder="Search document"
              className="search-input"
            />
          </SearchbarContainer>
        </Box>
        {!documents.length && (
          <EmptyState message="You have not created any documents or all available documents are already selected." />
        )}
        {loading ? (
          <Box position="relative" height="60vh">
            <Loader />
          </Box>
        ) : (
          <InfiniteScrollWrapper hasMore={hasNextPage} next={handlePageEndReached} height="60vh">
            <DocumentTable
              columns={columns}
              documents={documents}
              selectedDocumentIds={selectedIds}
              onSelectionChange={setSelectedIds}
              multiselect={multiselect}
            />
          </InfiniteScrollWrapper>
        )}
      </>
    );
  }

  return (
    <DialogDefault
      open={open}
      fullWidth
      iconName="plus"
      maxWidth="xl"
      title="Add documents"
      onClose={onClose}
    >
      <DialogContent>{dialogContent}</DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <ThemeButton onClick={onClose} color="BLUE_ICE" size="large">
          Cancel
        </ThemeButton>

        <ThemeButton color="PRIMARY" size="large" onClick={onSubmit}>
          Add selected documents {selectedIds.length > 0 ? `(${selectedIds.length})` : ''}
        </ThemeButton>
      </DialogActions>
    </DialogDefault>
  );
};

export default SelectDocumentsDialog;
